/**
 * LangContext - single source of language state for the app.
 *
 * - Initial value comes from `detectLanguage()` (URL > localStorage > navigator).
 * - `setLang` writes to localStorage AND syncs the URL `?lang=` so deep-links
 *   carry the choice.
 * - Switching is in-place, no reload: every data loader keyed by language
 *   (`loadAppContent`, `loadContent`, `loadQuizPages`) caches per language and
 *   every hook that reads them lists `lang` in its deps. `{ reload: true }`
 *   stays available for a caller that would rather start from a clean slate.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { detectLanguage, persistLanguage, Lang, SUPPORTED_LANGS } from './detectLanguage';

interface LangContextValue {
    lang: Lang;
    setLang: (next: Lang, opts?: { reload?: boolean }) => void;
    supported: readonly Lang[];
}

const LangContext = createContext<LangContextValue | null>(null);

export const LangProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [lang, setLangState] = useState<Lang>(() => detectLanguage());

    // `index.html` ships `lang="en"`. Screen readers pick a voice from it and
    // browsers offer to translate the page on it, so it has to follow the
    // choice rather than describe the build.
    useEffect(() => {
        if (typeof document !== 'undefined') document.documentElement.lang = lang;
    }, [lang]);

    const setLang = useCallback((next: Lang, opts?: { reload?: boolean }) => {
        if (next === lang) return;

        persistLanguage(next);

        // Sync the URL so the choice is shareable.
        try {
            const url = new URL(window.location.href);

            url.searchParams.set('lang', next);
            window.history.replaceState({}, '', url.toString());
        } catch {
            /* ignore - older browsers */
        }

        if (opts?.reload) {
            window.location.reload();

            return;
        }

        setLangState(next);
    }, [lang]);

    const value = useMemo<LangContextValue>(
        () => ({ lang, setLang, supported: SUPPORTED_LANGS }),
        [lang, setLang],
    );

    return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export function useLang(): LangContextValue {
    const ctx = useContext(LangContext);

    if (!ctx) {
        throw new Error('useLang must be used inside <LangProvider>');
    }

    return ctx;
}
